import Image, { type ImageOptions } from '@tiptap/extension-image'
import type { NodeType, Node as ProseMirrorNode } from '@tiptap/pm/model'
import {
  type EditorState,
  Plugin,
  PluginKey,
  TextSelection
} from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'
import { type NodeViewRendererProps, ReactRenderer } from '@tiptap/react'
import { fetchApiWithToken } from '~/api/utils'
import { ImageUploadNodeView } from './ImageUploadNodeView'

type ImageUploadOptions = ImageOptions & {
  legacyUrl: string | null | undefined
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    imageUpload: {
      insertImageUploads: (files: File[]) => ReturnType
    }
  }
}

const UPLOAD_ERROR_MESSAGE = "L'envoi de l'image a échoué"
const FAKE_PROGRESS_CEILING = 90
const FAKE_PROGRESS_INTERVAL_MS = 500

const createUploadAttribute = <T>(defaultValue: T) => ({
  default: defaultValue,
  parseHTML: () => defaultValue,
  renderHTML: () => ({})
})

const parsePxDimension = (
  el: HTMLElement,
  dimension: 'width' | 'height'
): number | null => {
  const match = el.style[dimension].match(/^(\d+)px$/)
  if (match?.[1]) {
    return Number.parseInt(match[1], 10)
  }
  const attr = el.getAttribute(dimension)
  if (!attr) return null
  const n = Number.parseInt(attr, 10)
  return Number.isNaN(n) ? null : n
}

const isImageFile = (file: File) => file.type.startsWith('image/')

const readAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

const createImageUploadHandler = (
  nodeType: NodeType,
  legacyUrl: string | null | undefined
) => {
  const findNodeByUploadId = (
    uploadId: string,
    state: EditorState
  ): number | null => {
    let foundPos: number | null = null
    state.doc.descendants((node, pos) => {
      if (node.type === nodeType && node.attrs.uploadId === uploadId) {
        foundPos = pos
        return false
      }
    })
    return foundPos
  }

  const updateNodeAttrs = (
    uploadId: string,
    attrs: Record<string, unknown>,
    view: EditorView
  ) => {
    if (view.isDestroyed) return
    const pos = findNodeByUploadId(uploadId, view.state)
    if (pos === null) return
    const node = view.state.doc.nodeAt(pos)
    if (!node) return
    view.dispatch(
      view.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...attrs })
    )
  }

  const toFinalUrl = (url: string) =>
    url.includes('/admin') && legacyUrl ? `${legacyUrl}${url}` : url

  const uploadImage = async (
    file: File,
    view: EditorView,
    uploadId: string
  ) => {
    const formData = new FormData()
    formData.append('file', file)

    let progress = 10
    updateNodeAttrs(uploadId, { uploadProgress: progress }, view)

    const fakeProgress = setInterval(() => {
      progress = Math.min(
        FAKE_PROGRESS_CEILING,
        progress + Math.floor(Math.random() * 10)
      )
      updateNodeAttrs(uploadId, { uploadProgress: progress }, view)
    }, FAKE_PROGRESS_INTERVAL_MS)

    try {
      const result = await fetchApiWithToken<{
        url: string
        id: number
        filename: string
      }>({
        url: '/v1/images/upload',
        method: 'POST',
        data: formData
      })

      clearInterval(fakeProgress)
      updateNodeAttrs(
        uploadId,
        {
          src: toFinalUrl(result.url),
          uploading: false,
          uploadProgress: 100,
          uploadError: null,
          uploadId: null
        },
        view
      )
    } catch (error) {
      clearInterval(fakeProgress)
      console.error('Image upload failed', error)
      updateNodeAttrs(
        uploadId,
        { uploading: false, uploadError: UPLOAD_ERROR_MESSAGE },
        view
      )
    }
  }

  const insertImageFile = async (file: File, view: EditorView) => {
    let src: string
    try {
      src = await readAsDataUrl(file)
    } catch (error) {
      console.error('Image read failed', error)
      return
    }
    if (view.isDestroyed) return

    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
    const imageNode = nodeType.create({
      src,
      uploading: true,
      uploadProgress: 0,
      uploadId
    })

    view.dispatch(view.state.tr.replaceSelectionWith(imageNode))
    await uploadImage(file, view, uploadId)
  }

  const handleImageFiles = (files: File[], view: EditorView) => {
    const imageFiles = files.filter(isImageFile)
    if (imageFiles.length === 0) return false

    void imageFiles.reduce(
      (chain, file) => chain.then(() => insertImageFile(file, view)),
      Promise.resolve()
    )
    return true
  }

  return { handleImageFiles }
}

export const ImageUpload = Image.extend<ImageUploadOptions>({
  name: 'imageUpload',

  addOptions() {
    return {
      inline: false,
      allowBase64: false,
      HTMLAttributes: {},
      resize: false,
      ...this.parent?.(),
      legacyUrl: null
    }
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('src'),
        renderHTML: ({ src }: { src: string | null }) => (src ? { src } : {})
      },
      width: {
        default: null,
        parseHTML: (el: HTMLElement) => parsePxDimension(el, 'width'),
        renderHTML: ({ width }: { width: number | null }) =>
          width ? { style: `width: ${width}px` } : {}
      },
      height: {
        default: null,
        parseHTML: (el: HTMLElement) => parsePxDimension(el, 'height'),
        renderHTML: ({ height }: { height: number | null }) =>
          height ? { style: `height: ${height}px` } : {}
      },
      uploading: createUploadAttribute(false),
      uploadProgress: createUploadAttribute(0),
      uploadError: createUploadAttribute<string | null>(null),
      uploadId: createUploadAttribute<string | null>(null)
    }
  },

  addNodeView() {
    const parentFactory = this.parent?.()
    if (!parentFactory) return null

    const isPending = (node: ProseMirrorNode) =>
      node.attrs.uploading === true || node.attrs.uploadError !== null

    return (props: NodeViewRendererProps) => {
      const { node, editor, getPos } = props

      if (!isPending(node)) {
        return parentFactory(props)
      }

      const onRemove = () => {
        const pos = getPos()
        if (pos === undefined) return
        editor
          .chain()
          .focus()
          .deleteRange({ from: pos, to: pos + node.nodeSize })
          .run()
      }

      const component = new ReactRenderer(ImageUploadNodeView, {
        props: { node, onRemove },
        editor
      })

      return {
        dom: component.element,
        update: (updatedNode: ProseMirrorNode) => {
          if (updatedNode.type !== node.type || !isPending(updatedNode)) {
            return false
          }
          component.updateProps({ node: updatedNode, onRemove })
          return true
        },
        destroy: () => component.destroy()
      }
    }
  },

  addCommands() {
    return {
      insertImageUploads:
        (files: File[]) =>
        ({ view }) => {
          const { handleImageFiles } = createImageUploadHandler(
            this.type,
            this.options.legacyUrl
          )
          return handleImageFiles(files, view)
        }
    }
  },

  addProseMirrorPlugins() {
    const { handleImageFiles } = createImageUploadHandler(
      this.type,
      this.options.legacyUrl
    )

    return [
      new Plugin({
        key: new PluginKey('imageUploadHandler'),
        props: {
          handlePaste: (view, event) => {
            const files = Array.from(event.clipboardData?.files ?? []).filter(
              isImageFile
            )
            if (files.length === 0) return false
            event.preventDefault()
            return handleImageFiles(files, view)
          },
          handleDrop: (view, event) => {
            const files = Array.from(event.dataTransfer?.files ?? []).filter(
              isImageFile
            )
            if (files.length === 0) return false
            event.preventDefault()

            const dropPos = view.posAtCoords({
              left: event.clientX,
              top: event.clientY
            })
            if (dropPos) {
              view.dispatch(
                view.state.tr.setSelection(
                  TextSelection.near(view.state.doc.resolve(dropPos.pos))
                )
              )
            }
            return handleImageFiles(files, view)
          }
        }
      })
    ]
  }
})
